import {
  DOCUMENT_CONTENT_TYPES,
  DOCUMENT_MAX_BYTES,
  documentRecord,
  documentUploadOutput,
  documentUrlOutput,
  routes,
  type DocumentRecord,
} from '@gg/shared';
import { api, ApiRequestError } from './api';

/** Client-side check before asking for an upload URL; the API checks again (P-12). */
export function fileProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!(file.type in DOCUMENT_CONTENT_TYPES)) return 'profile.error.fileType';
  if (file.size > DOCUMENT_MAX_BYTES) return 'profile.error.fileTooLarge';
  if (file.size === 0) return 'profile.error.fileType';
  return null;
}

/**
 * Certificate upload (P-12): ask for a presigned PUT, send the file straight to the private
 * bucket, then confirm so the API checks it and puts it in the review queue.
 */
export async function uploadCertificate({
  file,
  issuedOn,
}: {
  file: File;
  /** The date written on the certificate: it sets its expiry (one year). */
  issuedOn: string;
}): Promise<DocumentRecord> {
  const problem = fileProblem(file);
  if (problem) throw new ApiRequestError(400, 'invalid_file', problem);
  const target = await api(routes.documentsUploadUrl, {
    method: 'POST',
    body: {
      issuedOn,
      fileName: file.name || 'certificate',
      contentType: file.type,
      sizeBytes: file.size,
    },
    schema: documentUploadOutput,
  });
  const put = await fetch(target.uploadUrl, {
    method: 'PUT',
    headers: target.headers,
    body: file,
  }).catch(() => null);
  if (!put?.ok) throw new ApiRequestError(put?.status ?? 0, 'upload_failed', 'Upload failed');
  return api(routes.documentComplete(target.document.id), {
    method: 'POST',
    schema: documentRecord,
  });
}

/**
 * Opens the certificate on a presigned GET (10 min). The tab is opened before the request so
 * mobile browsers don't block it as a pop-up.
 */
export async function openDocument(documentId: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { url } = await api(routes.documentUrl(documentId), { schema: documentUrlOutput });
    if (tab) tab.location.href = url;
    else window.location.href = url;
  } catch (e) {
    tab?.close();
    throw e;
  }
}
